import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { isInAppBrowser } from '../../utils/browserDetector';
import { useTheme } from '../../context/ThemeContext';

const PWAInstallPrompt = () => {
  const location = useLocation();
  const { currentTheme } = useTheme();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [hasCheckedPrompt, setHasCheckedPrompt] = useState(false);

  useEffect(() => {
    // Reset when route changes
    setHasCheckedPrompt(false);
    setShowInstallPrompt(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      
      // Log for debugging
      console.log('PWA install prompt available');
      
      // ถ้าเปิดจาก In-App Browser (LINE/Facebook) ให้แสดง prompt ทันที
      // ถ้าไม่ใช่ ให้รอสักครู่ก่อนแสดง (เพื่อไม่ให้รบกวน user)
      if (isInAppBrowser()) {
        setShowInstallPrompt(true);
      } else {
        // Delay สำหรับหน้า "/" เพื่อให้ user เห็นหน้า welcome ก่อน
        const delay = location.pathname === '/' ? 3000 : 2000;
        setTimeout(() => {
          setShowInstallPrompt(true);
        }, delay);
      }
      
      setHasCheckedPrompt(true);
    };

    // Only add listener if not already installed
    if (!window.matchMedia('(display-mode: standalone)').matches) {
      window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      
      // Fallback: ถ้า after 5 seconds ยังไม่มี event และไม่ใช่ In-App Browser
      // ให้แสดง prompt เอง (สำหรับกรณีที่ browser ไม่ support beforeinstallprompt)
      if (!isInAppBrowser()) {
        const fallbackTimer = setTimeout(() => {
          if (!hasCheckedPrompt && !deferredPrompt) {
            // ตรวจสอบว่าเป็น mobile device และยังไม่ได้ติดตั้ง
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            if (isMobile && !window.matchMedia('(display-mode: standalone)').matches) {
              // แสดง prompt แบบ manual (แนะนำให้ user ติดตั้งเอง)
              const delay = location.pathname === '/' ? 3000 : 2000;
              setTimeout(() => {
                setShowInstallPrompt(true);
              }, delay);
            }
          }
        }, 5000);
        
        return () => {
          clearTimeout(fallbackTimer);
        };
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, [location.pathname, hasCheckedPrompt, deferredPrompt]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Show the install prompt
      deferredPrompt.prompt();

      // Wait for the user to respond to the prompt
      const { outcome } = await deferredPrompt.userChoice;

      if (outcome === 'accepted') {
        console.log('User accepted the install prompt');
      } else {
        console.log('User dismissed the install prompt');
      }

      // Clear the deferredPrompt
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
    } else {
      // Fallback: ถ้าไม่มี deferredPrompt ให้แสดงคำแนะนำ
      // สำหรับ iOS Safari
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        alert('สำหรับ iOS: กดปุ่ม Share (แชร์) แล้วเลือก "Add to Home Screen" (เพิ่มไปที่หน้าจอหลัก)');
      } else if (/Android/i.test(navigator.userAgent)) {
        alert('สำหรับ Android: กดเมนู (⋮) แล้วเลือก "Add to Home screen" (เพิ่มไปที่หน้าจอหลัก)');
      } else {
        alert('กรุณาใช้เมนูของ Browser เพื่อติดตั้งแอป');
      }
      setShowInstallPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallPrompt(false);
  };

  if (!showInstallPrompt) return null;

  // Get theme colors
  const primaryColor = currentTheme.colors.primary;
  const textPrimary = currentTheme.colors['text-primary'];
  const backgroundColor = currentTheme.colors.background;
  const secondaryBg = currentTheme.colors['secondary-bg'];

  return (
    <div 
      className="fixed bottom-4 left-4 right-4 text-white px-4 py-3 rounded-lg shadow-lg z-50 sm:left-auto sm:right-4 sm:w-80"
      style={{ backgroundColor: primaryColor }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3 flex-1 min-w-0">
          <div 
            className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: secondaryBg, opacity: 0.3 }}
          >
            <svg 
              width="24" 
              height="24" 
              viewBox="0 0 24 24" 
              fill="none" 
              xmlns="http://www.w3.org/2000/svg"
              className="w-6 h-6"
            >
              {/* Phone body */}
              <rect x="7" y="2" width="10" height="20" rx="2" fill="#9333EA" stroke="#7C3AED" strokeWidth="1"/>
              {/* Screen grid */}
              <rect x="9" y="5" width="2" height="2" fill="#F97316" rx="0.3"/>
              <rect x="12" y="5" width="2" height="2" fill="#EAB308" rx="0.3"/>
              <rect x="15" y="5" width="2" height="2" fill="#3B82F6" rx="0.3"/>
              <rect x="9" y="8" width="2" height="2" fill="#10B981" rx="0.3"/>
              <rect x="12" y="8" width="2" height="2" fill="#EF4444" rx="0.3"/>
              <rect x="15" y="8" width="2" height="2" fill="#8B5CF6" rx="0.3"/>
              <rect x="9" y="11" width="2" height="2" fill="#F59E0B" rx="0.3"/>
              <rect x="12" y="11" width="2" height="2" fill="#06B6D4" rx="0.3"/>
              <rect x="15" y="11" width="2" height="2" fill="#EC4899" rx="0.3"/>
              {/* Home button */}
              <circle cx="12" cy="19" r="1.5" fill="#9333EA"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base leading-tight text-white">ติดตั้งแอป NurseApp</h3>
            <p className="text-sm opacity-95 mt-0.5 text-white">ใช้งานได้เร็วขึ้นและสะดวกกว่า</p>
          </div>
        </div>
        <div className="flex items-center space-x-2 ml-4 flex-shrink-0">
          <button
            onClick={handleDismiss}
            className="px-4 py-2 text-sm rounded-lg font-medium transition-opacity duration-200 hover:opacity-100"
            style={{ 
              backgroundColor: backgroundColor, 
              color: primaryColor,
              opacity: 0.9
            }}
          >
            ปิด
          </button>
          <button
            onClick={handleInstallClick}
            className="px-4 py-2 text-sm rounded-lg font-medium transition-colors duration-200 shadow-sm hover:opacity-90"
            style={{ 
              backgroundColor: backgroundColor, 
              color: primaryColor 
            }}
          >
            ติดตั้ง
          </button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;








